import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { deckFor } from "@/slides/Slides";
import { getScope, logout } from "@/lib/api";
import { useDeckNavigation } from "@/hooks/useDeckNavigation";
import { useFullscreen } from "@/hooks/useFullscreen";
import { DeckSidebar } from "@/components/deck/DeckSidebar";
import { DeckUtilityBar } from "@/components/deck/DeckUtilityBar";
import { DeckOverview } from "@/components/deck/DeckOverview";
import { DeckProgressBar } from "@/components/deck/DeckProgressBar";

export default function Deck({ prospect = null }) {
  const deck = useMemo(() => deckFor(prospect), [prospect]);
  const SLIDES = useMemo(() => deck.map((d) => d.c), [deck]);
  const titles = useMemo(
    () => deck.map((d, i) => ({ i, t: d.t, group: d.group })),
    [deck],
  );

  const wrapRef = useRef(null);
  const nav = useNavigate();
  const total = SLIDES.length;
  const scope = getScope();
  const isMaster = scope === "master";

  const [overview, setOverview] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const { isFull, toggle: toggleFull } = useFullscreen(wrapRef);

  const openOverview = useCallback(() => setOverview(true), []);
  const closeOverview = useCallback(() => setOverview(false), []);

  const { idx, direction, go } = useDeckNavigation({
    total,
    prospect,
    onRequestOverview: openOverview,
    onToggleFullscreen: toggleFull,
  });

  const handleLogout = async () => {
    try { await logout(); } catch (e) { /* proceed with client-side logout */ }
    nav("/login", { replace: true });
  };

  const Slide = SLIDES[idx];
  const slideProps = { idx, total, prospect, goNext: () => go(idx + 1) };

  return (
    <div ref={wrapRef} className="fixed inset-0 bg-cream-50 text-ink-900 overflow-hidden flex" data-testid="deck-container">
      <DeckSidebar
        deck={deck}
        idx={idx}
        go={go}
        onOpenOverview={openOverview}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        prospect={prospect}
      />

      <div className="relative flex-1 h-full overflow-hidden">
        <div className="absolute inset-0">
          <div key={idx} className={direction === "fwd" ? "slide-enter h-full w-full" : "slide-enter-back h-full w-full"}>
            <Slide {...slideProps} />
          </div>
        </div>

        <DeckUtilityBar
          isMaster={isMaster}
          isFull={isFull}
          onFullscreen={toggleFull}
          onLogout={handleLogout}
          onAdmin={() => nav("/admin")}
          onInbox={() => nav("/inbox")}
        />

        <button
          onClick={() => go(idx - 1)}
          disabled={idx === 0}
          data-testid="nav-prev"
          title="Previous (←)"
          className="absolute left-3 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-white/70 border border-ink-900/10 grid place-items-center text-ink-900/65 hover:text-sun-700 hover:border-sun-500 transition-colors disabled:opacity-0 disabled:pointer-events-none"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => go(idx + 1)}
          disabled={idx === total - 1}
          data-testid="nav-next"
          title="Next (→)"
          className="absolute right-3 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-white/70 border border-ink-900/10 grid place-items-center text-ink-900/65 hover:text-sun-700 hover:border-sun-500 transition-colors disabled:opacity-0 disabled:pointer-events-none"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        <DeckProgressBar idx={idx} total={total} />
      </div>

      {overview && (
        <DeckOverview
          titles={titles}
          total={total}
          idx={idx}
          onClose={closeOverview}
          onJump={(i) => { closeOverview(); go(i); }}
        />
      )}
    </div>
  );
}
